/*
 * The graphics settings and their ready-made profiles. Kept apart from the renderer (post.ts,
 * three.js) so the title screen's settings page can use them without loading three.js.
 */

/** Ready-made graphics profiles, from a "potato" for old laptops to everything on. */
export type GraphicsPreset = 'potato' | 'low' | 'medium' | 'high' | 'ultra';

export type VegetationQuality = 'off' | 'low' | 'medium' | 'high' | 'ultra';

/**
 * Every graphics option the player can set (Settings → Graphics).
 *
 * Gone since 0.37.3, by the players' own trial: shadows, temporal upscaling, ambient occlusion,
 * the tilt-shift blur, rendering at a high-DPI screen's full density and the automatic
 * resolution. Off, the picture was sharper and the frame rate three times higher (50 → 150 on a
 * fast card); on, they blurred it and cost more than they gave. The picture is drawn at the
 * screen's resolution with MSAA edges.
 */
export interface GraphicsSettings {
  /** The profile these came from, or 'custom' once one option was changed by hand. */
  preset: GraphicsPreset | 'custom';
  /** Share of the screen's resolution rendered (0.5–1): lower is faster and blurrier. */
  renderScale: number;
  /** Edge smoothing samples. */
  msaa: 0 | 2 | 4;
  bloom: boolean;
  /** How far trees keep their full shape ("off" hides the forests). */
  vegetation: VegetationQuality;
  /** The towns around the airport. */
  towns: boolean;
  /** Rain, snow and lightning on screen. */
  weather: boolean;
  birds: boolean;
  /** Frames a second at most (0: no limit). */
  fpsCap: 0 | 30 | 60 | 120;
}

export const GRAPHICS_PRESETS: Record<GraphicsPreset, Omit<GraphicsSettings, 'preset'>> = {
  potato: {
    renderScale: 0.5,
    msaa: 0,
    bloom: false,
    vegetation: 'off',
    towns: false,
    weather: false,
    birds: false,
    fpsCap: 30,
  },
  low: {
    renderScale: 0.75,
    msaa: 0,
    bloom: false,
    vegetation: 'low',
    towns: false,
    weather: true,
    birds: false,
    fpsCap: 60,
  },
  medium: {
    renderScale: 1,
    msaa: 2,
    bloom: true,
    vegetation: 'medium',
    towns: true,
    weather: true,
    birds: true,
    fpsCap: 0,
  },
  high: {
    renderScale: 1,
    msaa: 4,
    bloom: true,
    vegetation: 'high',
    towns: true,
    weather: true,
    birds: true,
    fpsCap: 0,
  },
  ultra: {
    renderScale: 1,
    msaa: 4,
    bloom: true,
    vegetation: 'ultra',
    towns: true,
    weather: true,
    birds: true,
    fpsCap: 0,
  },
};

export function presetSettings(preset: GraphicsPreset): GraphicsSettings {
  return { preset, ...GRAPHICS_PRESETS[preset] };
}

/** Saved settings of any age, made whole (old saves kept a preset name, or options now gone). */
export function normaliseGraphics(raw: unknown): GraphicsSettings {
  if (typeof raw === 'string' && raw in GRAPHICS_PRESETS)
    return presetSettings(raw as GraphicsPreset);
  if (raw && typeof raw === 'object') {
    const r = raw as Partial<GraphicsSettings>;
    const preset =
      r.preset && r.preset !== 'custom' && r.preset in GRAPHICS_PRESETS ? r.preset : null;
    const base = GRAPHICS_PRESETS[preset ?? 'high'];
    // Only the options there are now (an old save's shadows or upscaling are left behind).
    const out: GraphicsSettings = { preset: preset ?? 'custom', ...base };
    for (const key of Object.keys(base) as (keyof typeof base)[]) {
      if (r[key] !== undefined) (out as unknown as Record<string, unknown>)[key] = r[key];
    }
    return out;
  }
  return presetSettings('high');
}
